import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Trophy, CalendarDays, Users, Share2, Clock, Award, CheckCircle, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

// Import the competitions data
import { activeCompetitions } from "./components/CompetitionsSection";

// Image component with fallback
function CompetitionImage({ src, alt, fallbackSrc, className }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  
  const handleError = () => {
    if (!hasError && fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
  };
  
  return (
    <img 
      src={imgSrc} 
      alt={alt} 
      onError={handleError}
      className={className || "w-full h-full object-cover object-top"}
    />
  );
}

// Reusable CountdownTimer component
function CountdownTimer({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState(targetDate - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(targetDate - Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  // Get current date for comparison
  const now = new Date();
  
  // Check if the target date is in the past
  if (targetDate < now) {
    // For any past date, always show Competition Ended
    return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-500/50 px-3 py-1 font-medium rounded-md">Competition Ended</Badge>;
  }
  
  // For competitions with TBD date (June 1, 2025)
  if (targetDate.getFullYear() === 2025 && targetDate.getMonth() === 5 && targetDate.getDate() === 1) {
    return (
      <div className="flex flex-col items-center gap-1">
        <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
        <div className="text-sm font-medium text-yellow-600">TBD</div>
      </div>
    );
  }
  
  // If the date is in the future, show countdown
  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  return (
    <div className="flex flex-col items-center gap-1">
      <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
      <div className="grid grid-flow-col gap-1 text-center auto-cols-max">
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{days}</span>
          <span className="text-xs text-muted-foreground">days</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{hours}</span>
          <span className="text-xs text-muted-foreground">hrs</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{minutes}</span>
          <span className="text-xs text-muted-foreground">min</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{seconds}</span>
          <span className="text-xs text-muted-foreground">sec</span>
        </div>
      </div>
    </div>
  );
}

// Mock rules for competition details
const mockRules = [
  "Submissions must be original content",
  "Videos should be between 30 seconds and 2 minutes",
  "No explicit or offensive content allowed",
  "Participants must follow all community guidelines"
];

export default function CompetitionDetail() {
  const { id } = useParams();
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareTooltip, setShareTooltip] = useState("");
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  useEffect(() => {
    // In a real app, you would fetch the competition data from an API
    // For now, we'll use the mock data
    const foundCompetition = activeCompetitions.find(comp => comp.id === parseInt(id));
    
    if (foundCompetition) {
      setCompetition(foundCompetition);
    }
    
    setLoading(false);
  }, [id]);

  // Hide the share tooltip after 2 seconds
  useEffect(() => {
    if (showShareTooltip) {
      const timer = setTimeout(() => {
        setShowShareTooltip(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showShareTooltip]);

  // Share competition function
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = competition ? `Check out the ${competition.title} competition on Pagent` : 'Competition on Pagent';
    const shareText = competition ? `Join the ${competition.title} competition and win ${competition.prize}!` : 'Join this competition on Pagent!';

    // Try using the Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        setShareTooltip("Shared successfully!");
        setShowShareTooltip(true);
      } catch (error) {
        console.error("Error sharing:", error);
        // Fall back to clipboard if share was cancelled or failed
        copyToClipboard(shareUrl);
      }
    } else {
      // Fall back to clipboard on devices without Web Share API
      copyToClipboard(shareUrl);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setShareTooltip("Link copied to clipboard!");
        setShowShareTooltip(true);
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
        setShareTooltip("Failed to copy link");
        setShowShareTooltip(true);
      });
  };

  // Competition prize data
  const getPrizeData = () => {
    // Example for Ariana Grande competition
    if (competition && competition.title.includes("Ariana Grande")) {
      return {
        first: "$100",
        second: "$75",
        third: "$25",
        day: "April 13, 2025 (1-hour event)",
        submissionPeriod: "Ends before 5PM EST",
        votingPeriod: "From 5PM to 6PM EST"
      };
    }
    
    // Default prize structure for other competitions
    return {
      first: competition?.prize || "Cash Prize",
      second: "",
      third: "",
      day: competition?.date || "TBD",
      submissionPeriod: "Ends before 5PM EST",
      votingPeriod: "From 5PM to 6PM EST"
    };
  };

  if (loading) {
    return (
      <div className="container py-20 text-center">
        <p>Loading competition details...</p>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="container py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Competition Not Found</h2>
        <p className="mb-8">The competition you're looking for doesn't exist or has been removed.</p>
        <Link to="/competitions">
          <Button>Back to Competitions</Button>
        </Link>
      </div>
    );
  }

  const prizeData = getPrizeData();

  return (
    <div className="pb-20">
      {/* Back button - visible only on larger screens (sm and up) */}
      <div className="hidden sm:block bg-yellow-500 text-white px-4 py-3">
        <div className="container">
          <Link to="/competitions" className="inline-flex items-center text-white hover:text-white/80">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Competitions
          </Link>
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-yellow-500/90 to-yellow-600/90 py-16 md:py-20">
        <div className="container px-4 sm:px-6">
          <div className="flex flex-col items-center max-w-4xl mx-auto text-center">
            {/* Large rounded image above title */}
            {competition.image && (
              <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-3xl border-4 border-white shadow-xl overflow-hidden mb-4">
                <CompetitionImage 
                  src={competition.image} 
                  alt={competition.title} 
                  fallbackSrc={competition.fallbackImage || "/images/makeupcompetition1.png"}
                />
              </div>
            )}
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              {competition.title}
            </h1>
            
            {/* Prize display moved below title - bigger with white text */}
            <div className="mb-4 flex items-center justify-center">
              <div className="bg-white px-6 py-3 rounded-full shadow-md">
                <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-navy-700">Prize: {competition.prize}</span>
              </div>
            </div>
            
            {/* Simple black text for Video Submission instead of badge */}
            <p className="text-xl sm:text-2xl font-medium text-black mb-6 sm:mb-8">
              Video Submission
            </p>
            
            <div className="flex flex-wrap gap-6 sm:gap-8 justify-center text-white text-sm sm:text-base">
              <div className="flex items-center">
                <CalendarDays className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                <span className="text-base sm:text-lg">{competition.date}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Competition Status */}
      <div className="bg-muted py-4 sm:py-6">
        <div className="container px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-center max-w-4xl mx-auto">
            <div className="flex items-center mb-4 sm:mb-0">
              <Clock className="mr-2 h-5 w-5 text-navy-600" />
              <span className="font-medium">Status:</span>
              <div className="ml-2">
                <CountdownTimer targetDate={competition.targetDate} />
              </div>
            </div>
            
            <div className="flex gap-3 w-full sm:w-auto">
              <Button 
                size="lg" 
                className="bg-navy-600 hover:bg-navy-700 text-white flex-1 sm:flex-initial"
                onClick={() => window.open("https://apps.apple.com/ca/app/pagent/id6670614673", "_blank")}
              >
                {Date.now() > (competition.targetDate + (24 * 60 * 60 * 1000)) ? "View Past Results" : "Join Competition"}
              </Button>
              
              <TooltipProvider>
                <Tooltip open={showShareTooltip}>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="border-navy-200 text-navy-600" onClick={handleShare}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="flex items-center gap-2">
                    {shareTooltip === "Link copied to clipboard!" ? <Copy className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                    <p>{shareTooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>
      
      {/* Competition Details */}
      <div className="container py-8 sm:py-12 px-4 sm:px-6">
        <div className="space-y-6 sm:space-y-10 max-w-4xl mx-auto">
          {/* Participation Details */}
          <Card className="border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-navy-600 to-navy-700 p-4 sm:p-5">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Participation Details</h2>
            </div>
            <CardContent className="p-4 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
                {/* Competition Info */}
                <div className="space-y-5 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-navy-600 p-2 rounded-md shadow-sm flex-shrink-0">
                      <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg mb-1">Day</h3>
                      <p className="text-gray-700 text-sm sm:text-base">{prizeData.day}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-navy-600 p-2 rounded-md shadow-sm flex-shrink-0">
                      <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg mb-1">Submission Period</h3>
                      <p className="text-gray-700 text-sm sm:text-base">{prizeData.submissionPeriod}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-navy-600 p-2 rounded-md shadow-sm flex-shrink-0">
                      <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg mb-1">Voting Period</h3>
                      <p className="text-gray-700 text-sm sm:text-base">{prizeData.votingPeriod}</p>
                    </div>
                  </div>
                </div>
                
                {/* Prize Info - Made more distinct */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 sm:p-6 border border-blue-200 shadow-md">
                  <div className="flex items-center justify-center mb-4 sm:mb-6">
                    <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-navy-600 mr-2 sm:mr-3" />
                    <h3 className="text-xl sm:text-2xl font-bold text-navy-700">Prize</h3>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-blue-200 flex items-center">
                      <div className="bg-navy-600 text-white rounded-full h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h4 className="text-xs sm:text-sm text-navy-600 font-medium">1st Place</h4>
                        <p className="text-lg sm:text-xl font-bold text-gray-800">{prizeData.first}</p>
                      </div>
                    </div>
                    
                    {prizeData.second && (
                      <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-blue-200 flex items-center">
                        <div className="bg-navy-500 text-white rounded-full h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                          2
                        </div>
                        <div>
                          <h4 className="text-xs sm:text-sm text-navy-600 font-medium">2nd Place</h4>
                          <p className="text-lg sm:text-xl font-bold text-gray-800">{prizeData.second}</p>
                        </div>
                      </div>
                    )}
                    
                    {prizeData.third && (
                      <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-blue-200 flex items-center">
                        <div className="bg-navy-400 text-white rounded-full h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                          3
                        </div>
                        <div>
                          <h4 className="text-xs sm:text-sm text-navy-600 font-medium">3rd Place</h4>
                          <p className="text-lg sm:text-xl font-bold text-gray-800">{prizeData.third}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* How to Participate */}
          <Card className="border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-navy-600 to-navy-700 p-4 sm:p-5">
              <h2 className="text-xl sm:text-2xl font-bold text-white">How to Participate</h2>
            </div>
            <CardContent className="p-4 sm:p-8">
              <ol className="space-y-3 sm:space-y-4">
                <li className="flex items-start pb-3 border-b border-gray-100">
                  <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                    1
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">Download the Pagent app from the App Store.</span>
                </li>
                <li className="flex items-start pb-3 border-b border-gray-100">
                  <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                    2
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">Create your profile and verify your account.</span>
                </li>
                <li className="flex items-start pb-3 border-b border-gray-100">
                  <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                    3
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">Check the competition on the Play screen to view submission and voting times.</span>
                </li>
                <li className="flex items-start pb-3 border-b border-gray-100">
                  <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                    4
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">Submit your entry before the submission period ends.</span>
                </li>
                <li className="flex items-start">
                  <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                    5
                  </div>
                  <span className="text-gray-700 text-sm sm:text-base">The winner will be announced after the voting period is complete.</span>
                </li>
              </ol>
              
              <div className="mt-6 sm:mt-8 flex justify-center">
                <Button 
                  className="bg-navy-600 hover:bg-navy-700 text-white w-full sm:w-auto px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-md transition-all duration-200 hover:shadow-lg"
                  onClick={() => window.open("https://apps.apple.com/ca/app/pagent/id6670614673", "_blank")}
                >
                  Download the Pagent App
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Rules Section */}
          <Card className="border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-navy-600 to-navy-700 p-4 sm:p-5">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Competition Rules</h2>
            </div>
            <CardContent className="p-4 sm:p-8">
              <ul className="space-y-3 sm:space-y-4">
                {mockRules.map((rule, index) => (
                  <li key={index} className="flex items-start pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className="bg-navy-600 text-white rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                      {index + 1}
                    </div>
                    <span className="text-gray-700 text-sm sm:text-base">{rule}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Related Competitions */}
      <div className="bg-muted/30 py-8 sm:py-12">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">Similar Competitions</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {activeCompetitions
                .filter(comp => comp.id !== competition.id)
                .slice(0, 3)
                .map(comp => (
                  <Card key={comp.id} className="overflow-hidden">
                    <div className="h-32 sm:h-40 bg-muted">
                      {comp.image ? (
                        <CompetitionImage 
                          src={comp.image} 
                          alt={comp.title} 
                          fallbackSrc={comp.fallbackImage || "/images/makeupcompetition1.png"}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-yellow-500/20 text-navy-600 text-xl font-bold">
                          {comp.category}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3 sm:p-4">
                      <Badge className="bg-navy-100 text-navy-600 border-0 mb-2 text-xs sm:text-sm">
                        {comp.category}
                      </Badge>
                      <h3 className="font-bold text-sm sm:text-base mb-2">{comp.title}</h3>
                      <Link to={`/competitions/${comp.id}`}>
                        <Button variant="ghost" className="w-full mt-2 text-sm sm:text-base py-1 h-auto text-navy-600 hover:bg-navy-50">View Details</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add a mobile-only fixed button at the bottom for navigation back */}
      <div className="sm:hidden fixed bottom-4 left-4 z-50">
        <Link to="/competitions">
          <Button size="sm" className="bg-navy-600 hover:bg-navy-700 text-white rounded-full shadow-lg p-3 h-auto w-auto">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
      </div>
    </div>
  );
} 